import React, { startTransition, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { bind, bindAll } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { ProseMirrorContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import type { EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import type { AIPlugin } from '../../../aiPluginType';
import { getMonitorWatchmenCommands } from '../../../pm-plugins/ai-monitor-watchmen/commands';
import { aiExperienceDecorationPluginKey } from '../../../pm-plugins/decoration/decoration-plugin-key';
import { useInEditorDirectStreaming } from '../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import type { EditorPluginAIProvider, EndExperience } from '../../../types/types';
import {
	AIExperienceCommonDataContext,
	useAIExperienceCommonDataContext,
} from '../../hooks/useAIExperienceCommonData';
import type { AIExperienceCommonDataContextData } from '../../hooks/useAIExperienceCommonData';
import { useContentComponentContext } from '../../hooks/useContentComponentContext';
import { CONFIG_ITEM_KEYS } from '../../prebuilt/config-items/config-item-keys';
import type {
	AnalyticsContext,
	EditorPluginAIConfigItem,
	SelectionType,
} from '../../prebuilt/config-items/config-items';
import { createFreeGenerateEditorAgentConfigItem } from '../../prebuilt/config-items/prompts/free-generate-editor-agent/free-generate-editor-agent';
import type {
	InvokedFrom,
	InvokedFromTriggerMethod,
} from '../../utils/analytics/analyticsFlowTypes';
import {
	AnalyticsFlowContextProvider,
	useAnalyticsFlow,
	useCreateAnalyticsFlow,
} from '../../utils/analytics/analyticsFlowUtils';
import { rangeCharWordCount, useFireAIAnalyticsEvent } from '../../utils/analytics/utils';
import { FAILURE_REASON } from '../../utils/errors';
import { getPMFragmentWithFallback } from '../../utils/get-pm-fragment-with-fallback';
import { publishAiExperienceLlmComplete } from '../../utils/publish-ai-experience-llm-complete';
import { adfResponseToFragment } from '../../utils/request/adf-response';
import { getLoadingMessage } from '../../utils/request/get-loading-message';
import { useStreamFlowPacerRef } from '../../utils/request/stream-flow-pacer';
import { getSelectionCategory, getSelectionNodeTypes } from '../../utils/selection';
import { createStreaming } from '../../utils/streaming';
import type {
	AIExperienceMachineContext,
	AIExperienceMachineEvent,
	AIExperienceMachineState,
	Positions,
} from '../../utils/xstate/get-ai-experience-service';
import { ModalRegionErrorBoundary } from '../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { UFOLoadingStatusTracker } from '../UFOLoadingStatusTracker/UFOLoadingStatusTracker';

import { AcceptRejectScreenWithLogic } from './screens-with-logic/AcceptRejectScreenWithLogic';
import { DiscardScreenWithLogic } from './screens-with-logic/DiscardScreenWithLogic';
import { ErrorScreenWithLogic } from './screens-with-logic/ErrorScreenWithLogic';
import { InvalidInputScreenWithLogic } from './screens-with-logic/InvalidInputScreenWithLogic';
import { LoadingScreenWithLogic } from './screens-with-logic/LoadingScreenWithLogic';
import { NoImprovementScreenWithLogic } from './screens-with-logic/NoImprovementScreenWithLogic';
import { PreviewScreenWithLogic } from './screens-with-logic/PreviewScreenWithLogic';
import { PromptScreenWithLogic } from './screens-with-logic/PromptScreenWithLogic';

type ExperienceApplicationProps = {
	aiGroupId?: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: EditorAppearance;
	canInlineStreamChanges: () => boolean;
	configItem: EditorPluginAIConfigItem;
	context: AIExperienceMachineContext;
	defaultAgent?: EditorAgent;
	editorPluginAIProvider: EditorPluginAIProvider;
	// all props under here may change based on how we wire things together
	editorView: EditorView;
	/**
	 * Called to end the experience
	 *
	 * Note: the ExperienceApplication will do additional cleanup work after calling this in
	 * its react unmount cleanup.
	 */
	endExperience: EndExperience;
	/** Engagement platform plugin API for starting and stopping messages. */
	engagementPlatformApi?: AIExperienceCommonDataContextData['engagementPlatformApi'];
	getMentionNameDetails?: GetMentionNameDetails;
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	/**
	 * Initial positions of the user's selection
	 */
	initialPositions?: Positions;
	initialPrompt?: string;
	interactionSessionId?: string;
	lastTriggeredFrom?: InvokedFrom;
	/**
	 * Confidence score will be used passed in analytics events.
	 * It is currently only set when "Improve writing" is triggered from the
	 * 	"Improve writing" nudge.
	 */
	nudgeMetrics?: Record<string, number | undefined>;

	/**
	 * For empty selections, the start and end will be the same
	 */
	positions: Positions;
	/**
	 * The version of the prompt used for the AI experience.
	 */
	promptVersion?: string;
	providerFactory: ProviderFactory;
	// Context by `useAIExperienceService` which creates the xstate machine and passes here
	send: (event: AIExperienceMachineEvent) => void;
	state: AIExperienceMachineState;
	/**
	 * The id of the traceId received from backend to trace the AI experience through the backend to frontend.
	 */
	traceId?: string;
	triggeredFor?: NodeType;
	triggerMethod?: InvokedFromTriggerMethod;
	userADFInput?: JSONDocNode;
};

type ExperienceApplicationStateManagerProps = {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	context: AIExperienceMachineContext;
	initialPrompt?: string;
	modalRef?: React.RefObject<HTMLDivElement>;
	state: AIExperienceMachineState;
	streamFlowPacer: StreamFlowPacer | null;
	userADFInput?: JSONDocNode;
};
/**
 * Main function for Experience Application State
 */
export function ExperienceApplicationStateManager(
	props: ExperienceApplicationStateManagerProps,
): React.JSX.Element | null {
	const { api } = props;
	/**
	 * Note -- because we are on an old version of xstate -- when adding new states
	 * they will not automatically be typed.
	 *
	 * You need to add these in the `AIExperienceMachineState` type in the
	 * `get-experience-service.ts` file
	 **/
	const state = props.state;
	/**
	 * Note -- because we are on an old version of xstate -- when adding new events
	 * they will not automatically be typed.
	 *
	 * You need to add these in the `AIExperienceMachineEvent` type in the
	 * `get-experience-service.ts` file
	 **/
	const context = props.context;
	const aiExperienceCommonData = useAIExperienceCommonDataContext();

	const { formatMessage } = useIntl();

	const publish = usePublish('ai-mate');

	const { mountDecorations, updateStream, discardStream } = useInEditorDirectStreaming({
		api,
		configItem: props.context.configItem,
		isProgrammaticScroll: aiExperienceCommonData.isProgrammaticScroll,
		positions: props.context.positions,
		view: props.context.editorView,
		modalRef: props.modalRef,
		aiSessionId: aiExperienceCommonData.aiSessionId,
	});

	const autoAcceptAIChanges = aiExperienceCommonData.canInlineStreamChanges();

	React.useEffect(() => {
		const send = aiExperienceCommonData.sendToAIExperienceMachine;
		if (send && state === 'started state') {
			send({ type: 'kick off' });
		}
	}, [state, aiExperienceCommonData.sendToAIExperienceMachine]);

	/**
	 * Used to pass the backendModel to analytics and
	 * feedback submission metadata
	 */
	const backendModel = context?.configItem?.getBackendModel?.(!!context?.latestPromptResponse);

	const analyticsFlow = useAnalyticsFlow();

	React.useEffect(() => {
		analyticsFlow.updateCommonAttributes({
			backendModel,
			channelId: context.channelId,
			...analyticsFlow.getUserFlowCommonAttributes(context.configItem),
			autoAcceptAIChanges,
			inlineStreamingAvailable: true,
		});
	}, [analyticsFlow, backendModel, context.channelId, context.configItem, autoAcceptAIChanges]);

	const aiLifeCycleDynamicAttributesGetter = context.aiLifeCycleDynamicAttributesGetter;
	React.useEffect(() => {
		if (aiLifeCycleDynamicAttributesGetter) {
			analyticsFlow.registerAiLifeCycleDynamicAttributesGetter(aiLifeCycleDynamicAttributesGetter);
		}
	}, [analyticsFlow, aiLifeCycleDynamicAttributesGetter]);

	// Note -- the intention is to move the creation of the state machine higher
	// and for the ExperienceApplication (react component that appears in the modal)
	// to only be started once the machine is in the modal active state.
	if (state === 'started state') {
		return null;
	}

	const isModalActiveState = typeof state !== 'string' && state['modal active state'];

	if (!isModalActiveState) {
		throw new Error('AI machine state is not: modal active state or modal error state');
	}

	const experienceState = state['modal active state']['experience'];

	const isLastResponseTooSimilar =
		context.responseHistory.entries[context.responseHistory.entries.length - 1]?.responseTooSimilar;
	// We use the FG directly here rather then canInlineStreamChanges() because this new screen will be shown regardless if
	// the fallback modal is shown or not.
	if (isLastResponseTooSimilar) {
		return (
			<NoImprovementScreenWithLogic
				discardStream={discardStream}
				aiExperienceMachineContext={context}
			/>
		);
	}

	if (
		typeof experienceState !== 'string' &&
		'error state' in experienceState &&
		experienceState['error state'] === 'active'
	) {
		if (context.apiErrorInfo.failureReason === FAILURE_REASON.NO_TEXTUAL_CONTENT) {
			return (
				<InvalidInputScreenWithLogic
					errorInfo={context.apiErrorInfo}
					contextStatistics={context.contextStatistics}
				/>
			);
		}
		return <ErrorScreenWithLogic errorInfo={context.apiErrorInfo} />;
	}

	if (
		typeof state['modal active state'] === 'object' &&
		state['modal active state']['discard'] === 'active'
	) {
		return (
			<DiscardScreenWithLogic
				state={experienceState}
				channelId={context.channelId}
				editCount={context.editCount}
				retryPromptCount={context.retryPromptCount}
				history={context.responseHistory}
				latestPrompt={context.latestPrompt}
				onceDiscard={context.onceDiscard}
				streamFlowPacer={props.streamFlowPacer}
				discardStream={discardStream}
				api={api}
			/>
		);
	}

	if (typeof experienceState === 'string' && experienceState === 'command palette state') {
		return <PromptScreenWithLogic context={context} userADFInput={props.userADFInput} />;
	}

	if (typeof experienceState === 'object' && 'loading state' in experienceState) {
		const loadingState = experienceState['loading state'];
		if (loadingState === 'loading') {
			/**
			 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
			 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
			 */
			const childShortTitle =
				context.parentPresetPromptLabel && context.configItem.nestingConfig
					? formatMessage(context.configItem.nestingConfig.shortTitle)
					: '';
			const loadingMessage = getLoadingMessage({ status: context?.loadingStatus, formatMessage });

			return (
				<>
					<LoadingScreenWithLogic
						mountDecorations={mountDecorations}
						updateStream={updateStream}
						parentPresetPromptLabel={context.parentPresetPromptLabel}
						childShortTitle={childShortTitle}
						title={
							loadingMessage
								? loadingMessage
								: formatMessage(aiExperienceApplicationMessages.loadingTitle)
						}
						markdown={context.markdown}
						adfResponse={context.adfResponse}
						adfResponseNodeWrappers={context.adfResponseNodeWrappers}
						latestPrompt={context.latestPrompt}
						promptTrigger={context.promptTrigger}
						refinementCount={context.refinementCount}
						latestPromptRetried={context.latestPromptRetried}
						idMap={context.idMap}
						contextStatistics={context.contextStatistics}
						hideLoadingScreen={aiExperienceCommonData.hideLoadingScreen}
						api={api}
					/>
					<UFOLoadingStatusTracker loadingStatus={context.loadingStatus} />
				</>
			);
		}
	}

	if (typeof experienceState !== 'string' && 'review state' in experienceState) {
		if (updateStream) {
			let pmFragment;
			if (context.adfResponse) {
				pmFragment = adfResponseToFragment({
					schema: context.editorView.state.schema,
					adfResponse: context.adfResponse,
					adfResponseNodeWrappers: context.adfResponseNodeWrappers,
				});
			} else {
				pmFragment = getPMFragmentWithFallback({
					markdown: context.markdown || '',
					schema: context.editorView.state.schema,
					source: 'preview',
					fireAIAnalyticsEvent: () => {},
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					idMap: context.idMap!,
				}).pmFragment;
			}

			if (pmFragment) {
				// TODO: EDITOR-1814 - this should be in useEffect
				updateStream?.({
					fragment: pmFragment,
					hasUserScrolled: aiExperienceCommonData.isUserScroll.current,
					isFinalStream: true,
				});
			}

			const decorationPluginState = aiExperienceDecorationPluginKey.getState(
				context.editorView.state,
			);

			const pmStats = new ProseMirrorContentStatistics(pmFragment);
			const contentStatistics = pmStats.collectStatistics();

			// when auto accept is on, end immediately
			// when auto accept is off
			// - if it's staging first-time streaming, withAcceptReject is false, end immediately
			if (
				autoAcceptAIChanges ||
				!decorationPluginState?.withAcceptReject ||
				context.configItem.key === CONFIG_ITEM_KEYS.SUGGEST_A_TITLE
			) {
				analyticsFlow.addAttributes({
					modelIO: {
						contextStatistics: context.contextStatistics,
						previewMarkCounts: contentStatistics.markCounts,
						previewNodeCounts: contentStatistics.nodeCounts,
						previewTotalCharacterCounts: contentStatistics.totalCharacterCount,
					},
				});

				publishAiExperienceLlmComplete(
					context.configItem,
					context.markdown || '',
					context.editorView,
					aiExperienceCommonData.lastTriggeredFrom ?? '',
				);
				context.endExperience();
				return null;
			} else {
				// if there is nextSessionId, it means there is an ai session running
				// - we don't show the accept reject screen
				// if there is no nextSessionId, it means the ai session ended or there is no ai session at all (such as IW)
				// - then we can show the accept reject screen
				return decorationPluginState?.nextSessionId ? null : (
					<AcceptRejectScreenWithLogic
						aiExperienceMachineContext={context}
						backendModel={backendModel}
						discardStream={discardStream}
					/>
				);
			}
		} else {
			const action = context.rovoActions ? context.rovoActions[context.selectionType] : undefined;
			return (
				<PreviewScreenWithLogic
					aiExperienceMachineContext={context}
					isInputActive={experienceState['review state'] === 'refinement'}
					backendModel={backendModel}
					rovoAction={action}
					rovoPublish={publish}
				/>
			);
		}
	}

	throw new Error('unhandled state');
}

type ExperienceApplicationStateWrapperProps = ExperienceApplicationProps & {
	/** The analytics context for the experience */
	analyticsContext: AnalyticsContext;
};

function canReplaceBaseGenerate(aiProvider: EditorPluginAIProvider) {
	return aiProvider.isRovoEnabled && aiProvider.useFreeGenerateEditorAgent;
}

function useScrollDetection({ modalRef }: { modalRef: React.RefObject<HTMLDivElement> }) {
	const isUserScroll = useRef<boolean>(false);
	/**
	 * isProgrammaticScroll is set to true initially
	 * to detect any initial changes on render which affect
	 * scroll position. There will be multiple scroll events
	 * within a single user scroll so it will be set to false
	 * accordingly within a single scroll action.
	 */
	const isProgrammaticScroll = useRef<boolean>(true);

	useEffect(() => {
		const modalEl = modalRef.current;
		if (!modalEl) {
			return;
		}
		const scrollParentEl: HTMLElement | false = findOverflowScrollParent(modalEl);
		if (!scrollParentEl) {
			return;
		}
		isUserScroll.current = false;
		const unbind = bind(scrollParentEl, {
			type: 'scroll',
			listener: () => {
				if (!isProgrammaticScroll.current) {
					isUserScroll.current = true;
					unbind();
				}
				isProgrammaticScroll.current = false;
			},
		});
		return () => {
			unbind();
		};
	}, [modalRef]);

	return { isUserScroll, isProgrammaticScroll };
}

function focusEditor(
	editorView: EditorView,
	{ focusEditorContentArea }: { focusEditorContentArea: boolean } = {
		focusEditorContentArea: false,
	},
) {
	if (!editorView.hasFocus()) {
		editorView.focus();
	}

	// when we disable editor while streaming, document.body will be focused
	// we need to focus editor to make pressing esc to work
	const editorContentArea = editorView?.dom.closest<HTMLElement>('.ak-editor-content-area');
	if (
		editorContentArea &&
		!editorContentArea.contains(document.activeElement) &&
		focusEditorContentArea
	) {
		editorContentArea.focus();
	}
}

function focusEditorContentArea(editorView: EditorView) {
	// if it's already focused, do nothing
	const editorContentArea = editorView?.dom.closest<HTMLElement>('.ak-editor-content-area');
	if (editorContentArea?.contains(document.activeElement)) {
		return;
	}

	editorView.focus();

	// if the editor is currently disabled, the above focus call may fail (browser dependent)
	if (editorView.hasFocus()) {
		return;
	}

	if (editorContentArea && !editorContentArea?.contains(document.activeElement)) {
		editorContentArea?.focus();
	}
}

// document.body will be focused when we switch from one screen to another
// (can be triggered by clicking button or pressing Esc key)
// which will make pressing Esc not working because we're listening Esc on editor
// so we should focus editor when it's not focused
function useFocusEditor({
	modalRef,
	editorView,
	state,
	cancelRovoPrompt,
}: {
	cancelRovoPrompt?: () => void;
	editorView: EditorView;
	modalRef: React.RefObject<HTMLDivElement>;
	state: AIExperienceMachineState;
}) {
	// state is not stable, but we want to run the effect when we switch screen, so use str as the dependency
	const stateStr = JSON.stringify(state);
	useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		const modalHasFocus = modalRef.current.contains(document.activeElement);
		if (!modalHasFocus && stateStr.indexOf('modal active state') !== -1) {
			focusEditorContentArea(editorView);
		}
	}, [modalRef, editorView, stateStr]);

	useEffect(() => {
		if (cancelRovoPrompt) {
			focusEditorContentArea(editorView);
		}
	}, [cancelRovoPrompt, editorView]);

	useEffect(
		() => () => {
			editorView.focus();
		},
		[editorView],
	);
}

function ExperienceApplicationStateWrapper(props: ExperienceApplicationStateWrapperProps) {
	const { api, editorView, endExperience } = props;
	const intl = useIntl();
	const modalRef = React.useRef<HTMLDivElement>(null);
	const { cancelRovoPrompt } = useContentComponentContext();

	const positions = props.positions;
	const isEmpty = positions[0] === positions[1];
	const selectionType: SelectionType = isEmpty ? 'empty' : 'range';

	/**
	 * baseGenerate configItem is used as default configItem in command palette.
	 * Currently for every product, "freeGenarete" is used as baseGenerate.
	 * Recently, "useFreeGenerateEditorAgent" is introduced to allow
	 * 	freeGenerateEditorAgent configItem to be used as baseGenerate.
	 *  freeGenerateEditorAgent is like freeGeneerate, but it uses
	 * 	rovo agent instead of standard AI prompt.
	 *
	 * Can't set "freeGenerateEditorAgent" directly as baseGenerate in prebuilt config,
	 * 	because creating configItem requires agent to be passed in.
	 * But agents are not available at compile time or event when plugin is loaded.
	 * So we need to swap baseGenerate here.
	 */
	let configItem = props.configItem;
	let baseGenerate = props.editorPluginAIProvider.baseGenerate;
	if (props.defaultAgent && canReplaceBaseGenerate(props.editorPluginAIProvider)) {
		baseGenerate = createFreeGenerateEditorAgentConfigItem({
			agent: props.defaultAgent,
			product: props.editorPluginAIProvider.product,
			isInDocumentStreamingEnabled: props.editorPluginAIProvider.isInDocumentStreamingEnabled,
		});

		if (props.configItem.key === CONFIG_ITEM_KEYS.FREE_GENERATE) {
			configItem = baseGenerate;
		}
	}

	const { isUserScroll, isProgrammaticScroll } = useScrollDetection({ modalRef });

	const streamFlowPacerRef = useStreamFlowPacerRef();

	const analyticsFlow = useAnalyticsFlow();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const streamingHandler = createStreaming({
		fireAIAnalyticsEvent,
		aiInteractionID: analyticsFlow.getLastAiInteractionId(),
		streamFlowPacer: streamFlowPacerRef.current,
	});

	const { send, state, context } = props;

	// Send this synchronously
	useLayoutEffect(() => {
		send({
			type: 'initialise context',
			context: {
				analyticsContext: props.analyticsContext,
				aiProvider: props.editorPluginAIProvider,
				intl,
				editorView: props.editorView,
				positions: positions,
				isEmpty,
				selectionType,
				baseGenerate,
				configItem,
				aiLifeCycleDynamicAttributesGetter:
					props.editorPluginAIProvider?.aiLifeCycleDynamicAttributesGetter,
				markdown: '',
				latestPromptResponse: undefined,
				editCount: 0,
				refinementCount: 0,
				retryPromptCount: 0,
				latestPromptRetried: false,
				userInput: '',
				apiErrorInfo: {},
				responseHistory: { positionFromEnd: 0, entries: [], totalSize: 0, traversals: 0 },
				channelId: undefined,
				getMentionNameDetails: props.getMentionNameDetails,
				triggeredFor: props.triggeredFor,
				usePageKnowledge: true,
				endExperience: props.endExperience,
				streamingHandler,
			},
		});
		return () => {
			send({ type: 'reset' });
		};
		// This must ONLY run on mount/unmount to replicate the `useRef` behaviour of setting up the
		// machine once. If this re-runs it will reset our current state machine
		// Long term we should refactor this the react lifecycle isn't tied to xstate
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (props.defaultAgent && canReplaceBaseGenerate(props.editorPluginAIProvider)) {
			const newBaseGenerate = createFreeGenerateEditorAgentConfigItem({
				agent: props.defaultAgent,
				product: props.editorPluginAIProvider.product,
				isInDocumentStreamingEnabled: props.editorPluginAIProvider.isInDocumentStreamingEnabled,
			});
			send({
				type: 'update baseGenerate and configItem',
				baseGenerate: newBaseGenerate,
				configItem: newBaseGenerate,
			});
		}
	}, [props.defaultAgent, props.editorPluginAIProvider, send]);

	const [start, end] = props.positions;
	React.useEffect(() => {
		// Note -- the intention is to have the state machine created at a higher
		// level, and have the positions updated without being passed into this react
		// component.
		send({ type: 'update positions', positions: [start, end] });
		// Separate start/end as React will recognise tuples recreated
		// as a change, even if the values don't change
	}, [send, start, end]);

	useFocusEditor({ modalRef, editorView, state, cancelRovoPrompt });

	const { lastTriggeredFrom } = props;

	// By default when the experience is mounted we allow for an early escape attempt via space or other keys. If any other
	// key is pressed, we disable this early escape behaviour to allow for normal editor usage.
	const canEarlyEscape = React.useRef(true);

	React.useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		/**
		 * KeyDown (as opposed to KeyPress) event is used to handle the escape key so that
		 * other escape key handlers will not override this behaviour.
		 * Additionally this aligns with other escape listeners e.g. for closing typeahead popup.
		 */
		function handleEscapeKeyDown(event: KeyboardEvent) {
			/**
			 * These are the keys that can be used to trigger lists when typing
			 * in the Editor using markdown syntax.
			 * • uses Opt+8 in Mac
			 */
			const MARKDOWN_LIST_KEYS = ['-', '*', '•'];
			const MODIFIER_KEYS = ['Shift', 'Alt'];

			if (fg('platform_editor_ai_aifc_space_shortcut')) {
				// If any key other than the chosen keys are pressed, we cannot early escape so we will disable that behaviour
				if (
					canEarlyEscape.current &&
					event.key !== ' ' &&
					!MARKDOWN_LIST_KEYS.includes(event.key) &&
					!MODIFIER_KEYS.includes(event.key)
				) {
					canEarlyEscape.current = false;
				}

				if (
					!(
						event.key === 'Escape' ||
						(canEarlyEscape.current &&
							lastTriggeredFrom === 'spaceShortcut' &&
							(event.key === ' ' || MARKDOWN_LIST_KEYS.includes(event.key)))
					)
				) {
					return;
				}
			} else {
				// Exit early if the key is not Escape
				if (event.key !== 'Escape') {
					return;
				}
			}

			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			// If nextSessionId exists, let the SessionUIComponent manage the escape behaviour
			if (
				api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState
					.nextSessionId
			) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const modalActiveState = state?.['modal active state'];
			const experience = modalActiveState?.experience;

			// in command palette screen, AI modal renders RovoInlineChatPrompt component
			// and cancelRovoPrompt is from it
			if (experience === 'command palette state') {
				if (cancelRovoPrompt) {
					cancelRovoPrompt();
				} else {
					if (fg('platform_editor_ai_aifc_space_shortcut')) {
						endExperience({
							shouldIgnoreNextSpaceTrigger:
								(event.key === ' ' && lastTriggeredFrom === 'spaceShortcut') ||
								(event.key === 'Escape' && lastTriggeredFrom === 'spaceShortcut'),
						});
					} else {
						endExperience();
					}
				}

				if (
					MARKDOWN_LIST_KEYS.includes(event.key) &&
					fg('platform_editor_ai_aifc_space_shortcut')
				) {
					editorView.dispatch(editorView.state.tr.insertText(` ${event.key}`));
					return;
				}
				if (
					event.key === ' ' &&
					lastTriggeredFrom === 'spaceShortcut' &&
					fg('platform_editor_ai_aifc_space_shortcut')
				) {
					editorView.dispatch(editorView.state.tr.insertText(' '));
					return;
				}

				return;
			}

			const isLoading = 'loading state' in experience && experience['loading state'] === 'loading';
			const isPreview = 'review state' in experience && experience['review state'] === 'preview';

			// pressing ESC in loading or accept/reject screen should show the discard screen
			if (isLoading || isPreview) {
				// don't use `send({ type: 'escape' })` here because it will transition to `command palette state`
				if (modalActiveState?.discard === 'active') {
					send({ type: 'disable discard' });
				} else {
					send({ type: 'enable discard' });
				}
			}
		}

		/**
		 * Handle KeyUp event to prevent unexpected escape behaviours when
		 * there are other escape handlers outside of component (e.g. in product)
		 */
		function handleEscapeKeyUp(event: KeyboardEvent) {
			// Exit early if the key is not Escape
			if (event.key !== 'Escape') {
				return;
			}

			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			// If nextSessionId exists, let the SessionUIComponent manage the escape behaviour
			if (
				api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState
					.nextSessionId
			) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
		}

		function handleClickOutside(event: MouseEvent) {
			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			// If nextSessionId exists, let the SessionUIComponent manage the escape behaviour
			if (
				api?.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState
					.nextSessionId
			) {
				return;
			}

			const modalActiveState = state?.['modal active state'];
			const experience = modalActiveState?.experience;
			const isPreview =
				typeof experience === 'object' &&
				'review state' in experience &&
				experience['review state'] === 'preview';

			// click outside of the accept/reject screen should show the discard screen
			if (
				isPreview &&
				modalRef.current &&
				event.target &&
				!modalRef.current.contains(event.target as Node) &&
				modalActiveState?.discard === 'inactive'
			) {
				send({ type: 'enable discard' });
			}
		}

		// initially, editor is focused
		// when AI modal is loading and user presses ESC
		// we will show the AI modal discard screen
		// which will make AI modal to be auto focused
		// now if user presses ESC again,
		// we will return to the AI modal loading screen
		// so whenever user presses ESC, either the AI modal or the editor is focused
		// so the listener should be attached to the common ancestor of the AI Modal and the editor
		const node = editorView.dom.closest<HTMLElement>('.ak-editor-content-area');

		if (!node) {
			return;
		}

		return bindAll(node, [
			{ type: 'keydown', listener: handleEscapeKeyDown },
			{ type: 'keyup', listener: handleEscapeKeyUp },
			{ type: 'click', listener: handleClickOutside },
		]);
	}, [
		modalRef,
		state,
		send,
		context.promptTrigger,
		context.responseHistory,
		endExperience,
		editorView,
		cancelRovoPrompt,
		api,
		lastTriggeredFrom,
	]);

	const [isMounted, setIsMounted] = React.useState(false);
	React.useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		startTransition(() => {
			setIsMounted(true);
		});
	}, [modalRef]);

	React.useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		if (isMounted) {
			modalRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
		}
	}, [isMounted, modalRef]);

	const aiExperienceCommonData = React.useMemo(
		() => ({
			appearance: props.appearance,
			// configItem can be changed within state machine, so always get it from machine context.
			configItem: context.configItem,
			editorPluginAIProvider: props.editorPluginAIProvider,
			endExperience: props.endExperience,
			lastTriggeredFrom: props.lastTriggeredFrom,
			providerFactory: props.providerFactory,
			editorView: props.editorView,
			positions: props.positions,
			triggerMethod: props.triggerMethod,
			triggeredFor: props.triggeredFor,
			sendToAIExperienceMachine: send,
			modalRef,
			isUserScroll,
			isProgrammaticScroll,
			engagementPlatformApi: props.engagementPlatformApi,
			includeKnowledgeFromCurrentPage: context.usePageKnowledge,
			canInlineStreamChanges: props.canInlineStreamChanges,
			hideLoadingScreen: props.hideLoadingScreen,
			aiSessionId: props.interactionSessionId,
		}),
		[
			context.configItem,
			context.usePageKnowledge,
			props.editorView,
			props.editorPluginAIProvider,
			props.appearance,
			props.endExperience,
			props.lastTriggeredFrom,
			props.providerFactory,
			props.positions,
			props.triggerMethod,
			props.triggeredFor,
			props.engagementPlatformApi,
			props.canInlineStreamChanges,
			send,
			modalRef,
			isUserScroll,
			isProgrammaticScroll,
			props.hideLoadingScreen,
			props.interactionSessionId,
		],
	);

	React.useEffect(() => {
		const traceIds = props.traceId ? [props.traceId] : context.traceIds;
		const promptVersion = fg('platform_editor_ai_promptversion_in_analytics')
			? props.promptVersion
			: undefined;
		if (traceIds) {
			analyticsFlow.updateCommonAttributes({ traceIds, promptVersion });
		}
	}, [analyticsFlow, props.traceId, context.traceIds, props.promptVersion]);

	return (
		<div
			// We set these to ensure that when focus initially moves to
			// an element within the modal, that context about the modal
			// (e.g. "Atlassian Intelligence Dialog") is announced too
			role="dialog"
			// ED-19787
			// We would like to use aria-labelledBy so we can label this
			// dynamically for each screen, however it's not working as expected
			// in Chrome and Firefox (works in Safari). For now we'll use the dialog
			// role and make the first element of each screen a screen-reader accessible
			// heading for the experience.
			aria-label={intl.formatMessage(aiExperienceApplicationMessages.dialogAriaLabel)}
			ref={modalRef}
		>
			<AIExperienceCommonDataContext.Provider value={aiExperienceCommonData}>
				<ExperienceApplicationStateManager
					api={api}
					state={state}
					context={context}
					initialPrompt={props.initialPrompt}
					userADFInput={props.userADFInput}
					modalRef={modalRef}
					streamFlowPacer={streamFlowPacerRef.current}
				/>
			</AIExperienceCommonDataContext.Provider>
		</div>
	);
}

/**
 * Main component for Experience Application
 */
export function ExperienceApplication(props: ExperienceApplicationProps): React.JSX.Element {
	const { api, editorView, canInlineStreamChanges } = props;
	const monitorWatchmenCommands = useMemo(
		() => getMonitorWatchmenCommands(editorView),
		[editorView],
	);

	const analyticsFlow = useCreateAnalyticsFlow({
		invokeAttributes: {
			invokedFrom: props.lastTriggeredFrom,
			invokedFor: props.triggeredFor ? props.triggeredFor.name : 'text',
			triggerMethod: props.triggerMethod,
			experienceName: props.configItem.key,
			featureId: props.configItem.featureId,
			aiGroupId: props.aiGroupId,
			aiSessionId: props.interactionSessionId,
		},
		canInlineStreamChanges: props.canInlineStreamChanges,
		isInDocumentStreamingEnabled: true,
		onCommonAttributesUpdated: monitorWatchmenCommands?.syncCommonAttributes,
	});

	// initialPositions is updated many times during in-doc streaming,
	// but we only want to update the common analytics attributes once,
	// so we check if they've already been updated
	const commonAnalyticsAttributesUpdatedRef = useRef(false);
	React.useEffect(() => {
		if (commonAnalyticsAttributesUpdatedRef.current) {
			return;
		}
		commonAnalyticsAttributesUpdatedRef.current = true;

		const isInvokedFromEmptyPage = isEmptyDocument(props.editorView.state.doc);
		analyticsFlow.updateCommonAttributes({
			isInvokedFromEmptyPage,
			/**
			 * Confidence score will be used passed in analytics events.
			 * It is currently only set when "Improve writing" is triggered from the
			 * 	"Improve writing" nudge.
			 */
			nudgeMetrics: props.nudgeMetrics,
		});
		if (props.initialPositions) {
			const selectionCategory = getSelectionCategory(props.editorView, props.initialPositions);

			const { charCount, wordCount } = rangeCharWordCount(
				props.editorView.state.doc,
				props.initialPositions[0],
				props.initialPositions[1],
			);

			analyticsFlow.updateCommonAttributes({
				selectionCharCount: charCount,
				selectionWordCount: wordCount,
			});

			analyticsFlow.updateCommonAttributes({ isInvokedFromEmptyPage, selectionCategory });

			const selectionNodes = getSelectionNodeTypes(props.editorView.state, props.initialPositions);

			analyticsFlow.updateCommonAttributes({
				selectionNodes,
			});
		}
	}, [analyticsFlow, props.editorView, props.initialPositions, props.nudgeMetrics]);

	// finish analytics flow and fire remaining steps on unmount
	React.useEffect(() => {
		return () => {
			// have to postpone until animation frame to make sure we end analytics flow after child components unmount
			// which is where we enqueue last events
			window.requestAnimationFrame(() => {
				analyticsFlow.fireQueuedStep();
			});
		};
	}, [analyticsFlow]);

	// This makes sure that the selection preview is hidden when the experience is unmounted
	React.useEffect(() => {
		return () => {
			const tr = props.editorView.state.tr;
			tr.setMeta('hideSelectionPreview', true);
			props.editorView.dispatch(tr);
		};
	}, [props.editorView]);

	// focus editor when the AI modal is unmounted
	useEffect(() => {
		return () => {
			focusEditor(editorView);
			// when we set the selection with AI modal open,
			// prosemirror won't update the selection because editor is not focused
			// and when the selection is empty in a paragraph,
			// the cursor may be (randomly) placed somewhere else within the paragraph when we focus the editor
			// so we need to re-set the selection after we focus the editor to make prosemirror update the selection correctly
			// and we have to set the selection to somewhere else first
			// because prosemirror will skip the selection update if the selection doesn't change
			// https://hello.jira.atlassian.cloud/browse/EDITOR-1269
			if (
				editorView.state.selection instanceof TextSelection &&
				editorView.state.selection.empty &&
				editorView.state.selection.$to.parent.isTextblock &&
				editorView.state.selection.$to.parent.childCount > 0
			) {
				const to = editorView.state.selection.to;

				let tr = editorView.state.tr;
				tr.setSelection(TextSelection.create(tr.doc, 0));
				tr.setMeta('addToHistory', false).setMeta('scrollIntoView', false);
				editorView.dispatch(tr);

				tr = editorView.state.tr;
				tr.setSelection(TextSelection.create(tr.doc, to));
				tr.setMeta('addToHistory', false).setMeta('scrollIntoView', false);
				editorView.dispatch(tr);
			}
		};
	}, [editorView]);

	// turn diff off when unmount
	useEffect(() => {
		return () => {
			const autoAcceptAIChanges = canInlineStreamChanges();
			if (
				!autoAcceptAIChanges &&
				api?.trackChanges?.sharedState.currentState()?.isDisplayingChanges
			) {
				api?.core.actions.execute(api?.trackChanges?.commands.toggleChanges);
			}
		};
	}, [api, canInlineStreamChanges]);

	return (
		<ModalRegionErrorBoundary
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onCloseFallback={() => {
				props.endExperience();
			}}
		>
			<AnalyticsFlowContextProvider value={analyticsFlow}>
				<UFOSegment name="EditorAICommandPalette">
					<ExperienceApplicationStateWrapper
						// Ignored via go/ees005
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...props}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						analyticsContext={{
							aiSessionId: analyticsFlow.aiSessionId,
							aiGroupId: props.aiGroupId ?? analyticsFlow.aiSessionId,
						}}
					/>
				</UFOSegment>
			</AnalyticsFlowContextProvider>
		</ModalRegionErrorBoundary>
	);
}
